import CustomDataGridResult from '../../../../../../utils/Datagrid/CustomForecastDataGridResult';
import { useCallback, useEffect, useState } from 'react';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { parcelSynthesisTheme } from '../../../../../../utils/Datagrid/CustomForecastDataGridUtils';
import { checkObjectsEquality, getColorAverageBiomassInput } from 'utils/tools_functions';
import {
  CustomSimulationCell,
  CustomEditSelectCell,
} from '../../Datagrid/CustomSimulationDataGrid';
import bioLogo from 'assets/images/bio.png';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { StyledTooltip } from 'utils/Datagrid/CustomGenericDataGridUtils';
import { ORGANIC_STATES } from 'utils/GlobalVariables';
import { useTranslation } from 'react-i18next';

const hasCultureDifference = (row) => {
  if (!row?.isParent || !row?.children?.length) return false;
  return row.children.some((child) => child.previousCulture1 !== row.previousCulture1);
};

const DataGridParcelSynthesis = ({
  rows,
  columns,
  cultures,
  irrigationInformation,
  setHasChange,
  suggest,
  setSuggest,
  editedSynthesisCellsRef,
  isNitrogenASelectedConstraint,
}) => {
  const { t } = useTranslation();
  const [selectIndex, setSelectIndex] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const apiRef = useGridApiRef();

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      apiRef.current?.resetRowHeights();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [expandedRows]);

  const renderToggle = (rowId, isExpanded, label) => (
    <button type="button" onClick={() => toggleRow(rowId)} className="synthesis-toggle">
      {isExpanded ? (
        <KeyboardArrowDownIcon fontSize="small" />
      ) : (
        <KeyboardArrowRightIcon fontSize="small" />
      )}
      {label}
    </button>
  );

  const renderChildrenList = (children, getValue) => (
    <div className="synthesis-children-list">
      {children.map((child) => (
        <div key={child.previousCulture1}>{getValue(child)}</div>
      ))}
    </div>
  );

  const renderSelectEditCell = (params) => {
    return (
      <CustomEditSelectCell
        params={params}
        culturesList={cultures}
        selectIndex={selectIndex}
        isNitrogenASelectedConstraint={isNitrogenASelectedConstraint}
      />
    );
  };

  const processedColumns = columns?.map((column, index) => {
    let processedColumn = {
      field: column.field,
      headerName: column.headerName,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      cellClassName: () => {
        if (index === 2) {
          return 'MuiDataGrid-colCellSynthesis MuiDataGrid-colCellSynthesis-parcelle';
        }
      },
    };

    if (column.field === 'parcel') {
      processedColumn = {
        ...processedColumn,
        cellClassName: 'MuiDataGrid-colCellSynthesis-parcelle',
        renderCell: (params) => {
          const row = rows?.find((row) => row.id === params.id);
          return (
            <span>
              {params.value} {row?.isParent && <span>*</span>}
            </span>
          );
        },
      };
    }

    if (index === 0 || index === 2) {
      processedColumn.minWidth = 170;
      processedColumn.maxWidth = 170;
    }
    if (index === 1) {
      processedColumn.minWidth = 170;
      processedColumn.maxWidth = 170;
      processedColumn.valueFormatter = (value) => {
        if (typeof value === 'number') {
          return Number(value.toFixed(2));
        }
        return value;
      };
      processedColumn.renderCell = (params) => {
        const row = rows?.find((r) => r.id === params.id);
        const showBreakdown = hasCultureDifference(row);
        if (!showBreakdown) {
          return <span>{params.formattedValue}</span>;
        }
        const isExpanded = !!expandedRows[params.id];
        return (
          <div className="cell-box cell-box-division">
            <div className="cell-main-content">
              {renderToggle(params.id, isExpanded, params.formattedValue)}
            </div>
            {isExpanded &&
              renderChildrenList(row.children, (child) =>
                typeof child.surface === 'number' ? child.surface.toFixed(2) : child.surface,
              )}
          </div>
        );
      };
    }

    if (index === 2) {
      processedColumn = {
        ...processedColumn,
        index: index - 3,
        renderCell: (params) => {
          const row = rows?.find((r) => r.id === params.id);
          const showBreakdown = hasCultureDifference(row);
          const isExpanded = !!expandedRows[params.id];
          const displayValue = showBreakdown ? t('common.various') : params.value;

          return (
            <div className="cell-box cell-box-division">
              <div className="cell-main-content">
                {showBreakdown ? renderToggle(params.id, isExpanded, displayValue) : displayValue}
                <div className="cultures-icons-wrapper">
                  {(params.row?.organicState === 'conversion' ||
                    params.row?.organicState === 'organic') && (
                    <div className="culture-infos-icon">
                      <StyledTooltip
                        title={
                          params.row?.organicState === 'conversion'
                            ? t('simulation.result.common.bioConversion')
                            : t('simulation.common.bioCulture')
                        }
                        placement="top"
                      >
                        {params.row?.organicState === 'conversion' ? (
                          <SyncAltIcon className="dropletLogo" />
                        ) : (
                          <img src={bioLogo} alt={t('alt.bioLogo')} className="dropletLogo" />
                        )}
                      </StyledTooltip>
                    </div>
                  )}
                </div>
              </div>
              {showBreakdown &&
                isExpanded &&
                renderChildrenList(row.children, (child) => child.previousCulture1)}
            </div>
          );
        },
      };
    }

    if (index >= 3) {
      const isBiomassColumn = column.field === 'avgDryMatterPerCampaign';

      processedColumn = {
        ...processedColumn,
        index: index - 3,
        editable: !isBiomassColumn,
        minWidth: 180,
        ...(!isBiomassColumn && {
          renderCell: (params) =>
            CustomSimulationCell(params, editedSynthesisCellsRef, setSelectIndex),
          renderEditCell: renderSelectEditCell,
        }),
      };

      if (isBiomassColumn) {
        processedColumn = {
          ...processedColumn,
          cellClassName: (params) => {
            if (params.field === 'avgDryMatterPerCampaign') {
              return getColorAverageBiomassInput(params.row.averageBiomassInput);
            }
            return '';
          },
        };
      }
    }

    return processedColumn;
  });

  const processedRows = rows?.map((row, index) => {
    let processedRow = {
      id: row?.id,
      organicState: row.organicState,
      isExpanded: !!expandedRows[row?.id],
      childrenCount: row?.children?.length || 0,
    };

    irrigationInformation?.map((year) => {
      if (year?.irrigationDistribution?.[index] > 0) {
        processedRow = {
          ...processedRow,
          isIrrigated: true,
        };
      }
    });

    processedColumns?.forEach((column, idx) => {
      const fieldName = column.field;

      switch (fieldName) {
        case 'parcel':
          processedRow[fieldName] = row?.name;
          break;
        case 'surface':
          processedRow[fieldName] = row?.surface;
          break;
        case 'avgDryMatterPerCampaign':
          const averageBiomassInput = row?.averageBiomassInput || 0;
          processedRow['averageBiomassInput'] = averageBiomassInput;
          processedRow[fieldName] = averageBiomassInput + ' ' + 't MS/ha';
          break;
        default:
          if (idx === 2) {
            processedRow[fieldName] = row?.previousCulture1;
          }
          if (idx > 2) {
            const cultureByIndex = row?.cultures[idx - 3];
            if (cultureByIndex && Array.isArray(cultureByIndex)) {
              const isParcelDivision = cultureByIndex.some((culture) => culture.isSplit);
              processedRow[fieldName] = {
                isParcelDivision,
                cultures: cultureByIndex ? cultureByIndex : [],
              };
            }
          }
      }
    });
    return processedRow;
  });

  const processRowUpdate = useCallback(
    (updatedRow, previousRow) => {
      if (!checkObjectsEquality(updatedRow, previousRow)) {
        const simulatedCultures = Object.entries(updatedRow).filter(
          ([_, value]) => typeof value === 'object',
        );

        const simulatedCulturesObject = Object.fromEntries(simulatedCultures);

        const simulatedCulturesArray = Object.entries(simulatedCulturesObject).map(
          (entry) => entry[1].cultures,
        );

        const flattenedCulturesArray = simulatedCulturesArray.flat();

        const editedCell = flattenedCulturesArray.find(
          (editedCulture) => editedCulture?.isEdited === true,
        );

        if (!editedSynthesisCellsRef.current.includes(editedCell?.cellId)) {
          editedSynthesisCellsRef.current.push(editedCell?.cellId);
        }

        const updatedSuggest = structuredClone(suggest);

        let simulatedCulturesArrayClone = structuredClone(simulatedCulturesArray);

        const foundParcel = updatedSuggest.parcels.find((parcel) => parcel.id === updatedRow.id);

        simulatedCulturesArrayClone.forEach((cultures) => {
          cultures.forEach((culture) => {
            if (culture.cellId) {
              delete culture.cellId;
            }
            if (culture.isEdited) {
              delete culture.isEdited;
            }
          });
        });

        foundParcel.cultures = simulatedCulturesArrayClone;

        setSuggest(updatedSuggest);
        setHasChange(true);

        return updatedRow;
      }

      return previousRow;
    },
    [rows],
  );

  const getRowClassName = () => 'Mui-even';
  return (
    <div className="datagrid_container">
      <CustomDataGridResult
        rows={processedRows}
        columns={processedColumns}
        getRowClassName={getRowClassName}
        processRowUpdate={processRowUpdate}
        theme={parcelSynthesisTheme}
        simulationContext={'parcel-synthesis-box'}
        fullSize={true}
        fromSimulation
        externalApiRef={apiRef}
      />
    </div>
  );
};

export default DataGridParcelSynthesis;
